import { createContext, useState, useEffect, useContext, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import { fetcher } from "../utils/fetcher";

interface EventContextValue {
  events: Record<string, unknown> | null;
  volunteersEvents: Record<string, unknown> | null;
  eventsLoading: boolean;
  volunteersEventsLoading: boolean;
  eventsError: string | null;
  volunteersEventsError: string | null;
  setEventsError: (error: string | null) => void;
  setVolunteersEventsError: (error: string | null) => void;
  fetchEvents: (params?: Record<string, string>) => Promise<void>;
  fetchVolunteersEvents: (params?: Record<string, string>) => Promise<void>;
}

export const EventContext = createContext<EventContextValue | undefined>(undefined);

interface EventProviderProps {
  children: ReactNode;
}

export const EventProvider = ({ children }: EventProviderProps) => {
  const [events, setEvents] = useState<Record<string, unknown> | null>(null);
  const [volunteersEvents, setVolunteersEvents] = useState<Record<string, unknown> | null>(null);
  const [eventsLoading, setEventsLoading] = useState<boolean>(false);
  const [volunteersEventsLoading, setVolunteersEventsLoading] = useState<boolean>(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [volunteersEventsError, setVolunteersEventsError] = useState<string | null>(null);
  const { pathname } = useLocation();
  const { currentUser } = useContext(AuthContext)!;

  const fetchEvents = async (params: Record<string, string> = {}) => {
    setEventsError(null);
    setEventsLoading(true);

    const queryParams = new URLSearchParams(params).toString();

    let url = "/events";
    if (queryParams) {
      url += `?${queryParams}`;
    }
    if (currentUser?.userType === "organisation") {
      url += `${queryParams ? "&" : "?"}organiser_id=${currentUser?.userId}`;
    }

    try {
      const response = await fetcher<Record<string, unknown>>({ url });
      setEvents(response);
    } catch (error) {
      setEventsError(
        "Došlo k chybě při načítání událostí, zkuste to prosím později.",
      );
      console.error("Error fetching events data:", error);
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchVolunteersEvents = async (params: Record<string, string> = {}) => {
    setVolunteersEventsError(null);
    setVolunteersEventsLoading(true);

    const queryParams = new URLSearchParams(params).toString();

    let url = "/events/my_events";
    if (queryParams) {
      url += `?${queryParams}`;
    }

    try {
      const response = await fetcher<Record<string, unknown>>({ url });
      setVolunteersEvents(response);
    } catch (error) {
      setVolunteersEventsError(
        "Došlo k chybě při načítání událostí, zkuste to prosím později.",
      );
      console.error("Error fetching my events data:", error);
    } finally {
      setVolunteersEventsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchEvents();
    }
    if (currentUser?.userType === "volunteer") {
      fetchVolunteersEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    setEventsError(null);
    setVolunteersEventsError(null);
  }, [pathname]);

  return (
    <EventContext.Provider
      value={{
        events,
        volunteersEvents,
        eventsLoading,
        volunteersEventsLoading,
        eventsError,
        volunteersEventsError,
        setEventsError,
        setVolunteersEventsError,
        fetchEvents,
        fetchVolunteersEvents,
      }}
    >
      {children}
    </EventContext.Provider>
  );
};

